import React, { useCallback, useState, useEffect } from "react";
import { UnifiedPortfolioItem } from "@/types/unified-portfolio";
import UnifiedPortfolioCard from "./UnifiedPortfolioCard";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Info, X } from "lucide-react";

interface PortfolioLayoutListProps {
  items: UnifiedPortfolioItem[];
  onRemove: (id: string) => void;
  onEdit: (id: string) => void;
  onRename: (id: string, newTitle: string) => void;
  onSetFeatured: (id: string) => void;
  onUpdateStatus: (id: string, status: "draft" | "published") => void;
  onDuplicate: (id: string) => void;
  onPreview?: (item: UnifiedPortfolioItem) => void;
  onReorder?: (items: UnifiedPortfolioItem[]) => void;
  sortOrder: 'last_added' | 'last_edited' | 'alphabetical' | 'featured_first' | 'most_viewed';
  isProcessing?: boolean;
}

// Memoize the card for performance
const MemoUnifiedPortfolioCard = React.memo(UnifiedPortfolioCard);

// Sortable wrapper for unified portfolio cards
const SortableUnifiedPortfolioCard = React.memo(({
  item,
  index,
  onRemove,
  onEdit,
  onRename,
  onSetFeatured,
  onUpdateStatus,
  onDuplicate,
  onPreview,
  sortOrder,
  isProcessing,
}: any) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: item.id,
    animateLayoutChanges: () => true,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1 : 0,
    height: "100%",
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`h-full${isDragging ? " isDragging" : ""}`}
    >
      <MemoUnifiedPortfolioCard
        item={item}
        index={index}
        onRemove={onRemove}
        onEdit={onEdit}
        onRename={onRename}
        onSetFeatured={onSetFeatured}
        onUpdateStatus={onUpdateStatus}
        onDuplicate={onDuplicate}
        onPreview={onPreview}
        sortOrder={sortOrder}
        isDraggable={true}
        isProcessing={isProcessing}
      />
    </div>
  );
});

SortableUnifiedPortfolioCard.displayName = 'SortableUnifiedPortfolioCard';

const PortfolioLayoutList: React.FC<PortfolioLayoutListProps> = ({
  items,
  onRemove,
  onEdit,
  onRename,
  onSetFeatured,
  onUpdateStatus,
  onDuplicate,
  onPreview,
  onReorder,
  sortOrder,
  isProcessing = false,
}) => {
  // State to track callout visibility
  const [showCallout, setShowCallout] = useState(true);

  // Load dismissal state from localStorage
  useEffect(() => {
    const dismissed = localStorage.getItem('portfolio-layout-callout-dismissed');
    if (dismissed === 'true') {
      setShowCallout(false);
    }
  }, []);

  // Handle callout dismissal
  const handleDismissCallout = useCallback(() => {
    setShowCallout(false);
    localStorage.setItem('portfolio-layout-callout-dismissed', 'true');
  }, []);

  // Memoize handlers to avoid unnecessary re-renders
  const handleRemove = useCallback(onRemove, [onRemove]);
  const handleEdit = useCallback(onEdit, [onEdit]);
  const handleRename = useCallback(onRename, [onRename]);
  const handleSetFeatured = useCallback(onSetFeatured, [onSetFeatured]);
  const handleUpdateStatus = useCallback(onUpdateStatus, [onUpdateStatus]);
  const handleDuplicate = useCallback(onDuplicate, [onDuplicate]);
  const handlePreview = useCallback(onPreview, [onPreview]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px movement before drag starts
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag end with order updates
  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);

      if (oldIndex !== newIndex) {
        // If dragged to first position, auto-feature and publish if draft
        if (newIndex === 0) {
          const draggedItem = items[oldIndex];
          if (draggedItem.status === "draft") {
            handleUpdateStatus?.(String(active.id), "published");
          }
          handleSetFeatured?.(String(active.id));
        } else {
          // Otherwise, reorder items and remove featured status if applicable
          let reorderedItems = arrayMove(items, oldIndex, newIndex);

          // If the dragged item was featured, remove featured status
          if (items[oldIndex]?.is_featured) {
            reorderedItems = reorderedItems.map((item) =>
              item.id === active.id ? { ...item, is_featured: false } : item
            );
          }

          onReorder?.(reorderedItems);
        }
      }
    }
  }, [items, handleSetFeatured, handleUpdateStatus, onReorder]);

  if (!items?.length) return null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={items.map((item) => item.id)} strategy={rectSortingStrategy}>
        <div className="md:px-0">
          {showCallout && (
            <div className="flex items-center gap-2 mb-6 bg-sky-50 border border-sky-200 p-3 rounded-lg">
              <Info className="h-4 w-4 flex-shrink-0 mt-0.5 text-sky-800" />
              <p className="text-xs text-sky-800 flex-1">
                Drag items to change their order on your profile. Your featured item will always appear first.
              </p>
              <button
                onClick={handleDismissCallout}
                className="flex-shrink-0 text-sky-800 hover:text-sky-900 transition-colors"
                aria-label="Dismiss message"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((item, index) => (
            <SortableUnifiedPortfolioCard
              key={item.id}
              item={item}
              index={index}
              onRemove={handleRemove}
              onEdit={handleEdit}
              onRename={handleRename}
              onSetFeatured={handleSetFeatured}
              onUpdateStatus={handleUpdateStatus}
              onDuplicate={handleDuplicate}
              onPreview={handlePreview}
              sortOrder={sortOrder}
              isProcessing={isProcessing}
            />
          ))}
        </div>
        </div>
      </SortableContext>
    </DndContext>
  );
};

export default PortfolioLayoutList;
